import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import Consultations from "@/pages/Consultations";
import ConsultationFlow from "@/pages/ConsultationFlow";
import NewConsultation from "@/pages/NewConsultation";
import DynamicFormFill from "@/pages/DynamicFormFill";
import MobileUpload from "@/pages/MobileUpload";
import ConsentPublic from "@/pages/ConsentPublic";
import PatientEducation from "@/pages/PatientEducation";
import Plans from "@/pages/Plans";
import AdminPanel from "@/pages/AdminPanel";
import VoiceConsultation from "@/pages/VoiceConsultation";
import TreatmentPlanView from "@/pages/TreatmentPlanView";
import PatientReport from "@/pages/PatientReport";
import TreatmentPlans from "@/pages/TreatmentPlans";
import ClinicalForms from "@/pages/ClinicalForms";
import FormBuilder from "@/pages/FormBuilder";
import Reports from "@/pages/Reports";
import ReportDetail from "@/pages/ReportDetail";
import ContahaAI from "@/pages/ContahaAI";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import Appointments from "@/pages/Appointments";
import FollowUps from "@/pages/FollowUps";
import Tasks from "@/pages/Tasks";
import Team from "@/pages/Team";
import Surgeries from "@/pages/Surgeries";
import Documents from "@/pages/Documents";
import Templates from "@/pages/Templates";
import CaseDerived from "@/pages/CaseDerived";
import AIReports from "@/pages/AIReports";
import TreatmentInsights from "@/pages/TreatmentInsights";
import PatientCommunication from "@/pages/PatientCommunication";
import ConsultationWizard from "@/pages/ConsultationWizard";
import ConsentDocument from "@/pages/ConsentDocument";
import SignPhone from "@/pages/SignPhone";
import BuyPlan from "@/pages/BuyPlan";
import PurchaseSuccess from "@/pages/PurchaseSuccess";

export const PRICING_URL = "https://contaha.com/pricing";

function ExternalRedirect({ to }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

function RoutedToaster() {
  const loc = useLocation();
  const wizard = /^\/consultation\/[^/]+\/wizard/.test(loc.pathname);
  return <Toaster position={wizard ? "bottom-right" : "top-right"} offset={wizard ? 96 : undefined} richColors />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9F8] dark:bg-[#07131A]">
        <div className="h-8 w-8 rounded-full border-2 border-[#00A85A] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin && user.subscription_active === false) return <ExternalRedirect to={PRICING_URL} />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/m/upload/:token" element={<MobileUpload />} />
      <Route path="/c/:token" element={<ConsentPublic />} />
      <Route path="/s/:token" element={<SignPhone />} />
      <Route path="/buy/:planId" element={<BuyPlan />} />
      <Route path="/payment/success" element={<PurchaseSuccess />} />
      <Route
        element={
          <Protected>
            <AppLayout />
          </Protected>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/patients" element={<Patients />} />
        <Route path="/patients/:id" element={<PatientDetail />} />
        <Route path="/cases" element={<Cases />} />
        <Route path="/cases/:id" element={<CaseDetail />} />
        <Route path="/consultations" element={<Consultations />} />
        <Route path="/consultation/new" element={<NewConsultation />} />
        <Route path="/consultation/:id/form" element={<DynamicFormFill />} />
        <Route path="/consultation/:id/wizard" element={<ConsultationWizard />} />
        <Route path="/consents/:id" element={<ConsentDocument />} />
        <Route path="/consultation/:id" element={<VoiceConsultation />} />
        <Route path="/consultations/:id" element={<ConsultationFlow />} />
        <Route path="/forms" element={<ClinicalForms />} />
        <Route path="/forms/:id" element={<FormBuilder />} />
        <Route path="/assessments" element={<CaseDerived kind="assessment" />} />
        <Route path="/diagnoses" element={<CaseDerived kind="diagnosis" />} />
        <Route path="/treatment-plans" element={<TreatmentPlans />} />
        <Route path="/treatment-plan/:id" element={<TreatmentPlanView />} />
        <Route path="/treatment-plan/:id/patient-report" element={<PatientReport />} />
        <Route path="/implant-planning" element={<CaseDerived kind="implant" />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/reports/:id" element={<ReportDetail />} />
        <Route path="/education" element={<PatientEducation />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/surgeries" element={<Surgeries />} />
        <Route path="/follow-ups" element={<FollowUps />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/team" element={<Team />} />
        <Route path="/ai" element={<ContahaAI />} />
        <Route path="/ai-reports" element={<AIReports />} />
        <Route path="/treatment-insights" element={<TreatmentInsights />} />
        <Route path="/patient-communication" element={<PatientCommunication />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/billing" element={<Billing />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <RoutedToaster />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
